import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import type { QuestionInputField, QuestionInputObjectValue, QuestionInputValue } from '/index';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
import useAppContext from '/business/shared/hooks/useAppContext';

type VehicleTotalUsageSchemaProps = {
  vehicleFuelTypes: EmissionTypeOption[];
};

type DropdownValue = {
  displayName?: string;
  displayNameKey: string;
  isPlaceholder?: boolean;
  name: string | null;
};

function getStringInputValue(value: QuestionInputValue | QuestionInputObjectValue | undefined): string | null {
  return typeof value === 'string' ? value : null;
}

export default function useVehicleTotalUsageSchema({ vehicleFuelTypes: fuelTypes }: VehicleTotalUsageSchemaProps): {
  pageSchema: QuestionInputField[];
} {
  const { t } = useTranslation();
  const { questionsPage } = useAppContext();
  const [valueFuelType, setValueFuelType] = useState<string | null>(null);

  const validationValue =
    questionsPage?.hasCustomValidationValues && questionsPage?.validationValues?.otherFuels
      ? questionsPage?.validationValues?.otherFuels
      : 99999;

  // TODO - if hasEvCharging is enabled, allow electricity as a fuel type
  const fuelType: QuestionInputField = useMemo(() => {
    const fuelTypeValues: DropdownValue[] = [
      {
        name: null,
        isPlaceholder: true,
        displayNameKey: 'business:QuestionPage.forms.otherFuels.selectFuel',
      },
      ...fuelTypes.map(({ displayName, name }) => {
        return {
          name,
          displayName,
          displayNameKey: 'business:emissionFactors.buildingFuelTypes.'.concat(displayName),
        };
      }),
    ];
    return {
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para3',
      type: FIELD_TYPE_DROPDOWN,
      values: fuelTypeValues,
      propName: 'fuelId',
      onChangeCallback: (value) => {
        setValueFuelType(getStringInputValue(value));
      },
      element: 'fuel_type',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOFUELTYPE',
          errStr: t('business:Errors.fuelType.provideType'),
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRFUELTYPENOTFOUND',
          errStr: t('business:Errors.fuelType.notFound'),
        },
      ],
    };
  }, [fuelTypes, t]);

  const quantity: QuestionInputField = useMemo(() => {
    const fuelTypesUnits = fuelTypes.map((item) => {
      const typeUnitMapped = item?.units?.map((typeUnit) => {
        if (typeof typeUnit !== 'string') {
          return typeUnit;
        }
        const transLitDisplay = t('business:emissionFactors.volumes.'.concat(typeUnit), typeUnit);
        return {
          name: typeUnit,
          displayName: transLitDisplay,
        };
      });
      return {
        ...item,
        units: typeUnitMapped,
      };
    });
    return {
      element: 'quantity',
      propName: 'quantity',
      type: FIELD_TYPE_NUMBER,
      dropdown: {
        values: fuelTypesUnits?.find(({ name }) => name === valueFuelType)?.units,
        propName: 'quantityUnit',
        element: 'quantity_unit',
      },
      label: 'Quantity:',
      placeholderI18nKey: 'business:QuestionPage.forms.otherFuels.quantityPlaceholder',
      isDisabled: !valueFuelType,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOQUANTITY',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          errCode: 'ERRQUANTITYTOOLOW',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: 0,
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          errCode: 'ERRQUANTITYTOOHIGH',
          errStr: t('business:Errors.fuelType.fuelQuantity'),
          value: validationValue,
        },
      ],
    };
  }, [validationValue, fuelTypes, valueFuelType, t]);

  const dataSource: QuestionInputField = useMemo(() => {
    return {
      element: 'data_source',
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      labelI18nKey: 'business:QuestionPage.forms.otherFuels.para5',
      description: <></>,
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.provide'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.provide'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema: QuestionInputField[] = useMemo(
    () => [fuelType, quantity, dataSource],
    [fuelType, quantity, dataSource],
  );

  return {
    pageSchema,
  };
}
